'use client';

import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface StepShellProps {
  title: string;
  /** Optional single-line hint. Keep ≤ 90 chars. UI should carry the weight. */
  hint?: string;
  immutable?: boolean;
  children?: ReactNode;
}

/**
 * Minimal step container. The step number + label already live in the footer
 * ("3 of 6 · Board"), so the body only carries a title and optional one-liner.
 */
export function StepShell({ title, hint, immutable, children }: StepShellProps) {
  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <h1 className="font-serif text-[22px] text-amber-50 md:text-[26px]">{title}</h1>
          {immutable && (
            <span className="rounded-sm border border-amber-300/40 bg-amber-400/10 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.2em] text-amber-100/90">
              Immutable
            </span>
          )}
        </div>
        {hint && (
          <p className="text-[12px] text-amber-100/55">{hint}</p>
        )}
      </header>

      {children}
    </div>
  );
}

interface PlannedNoticeProps {
  children: ReactNode;
}

/** Single-line placeholder note for steps that aren't fully wired yet. */
export function PlannedNotice({ children }: PlannedNoticeProps) {
  return (
    <div
      className={cn(
        'rounded-[3px] border border-dashed border-amber-300/25',
        'bg-amber-400/[0.03] px-3 py-2',
        'font-mono text-[11px] uppercase tracking-[0.18em] text-amber-100/55',
      )}
    >
      {children}
    </div>
  );
}
